import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Target, ArrowLeft } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

const PortalAuth = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { signIn, signUp, signOut, logout } = useAuth();

  // Check if the URL has a reset token
  const hash = window.location.hash;
  const isPasswordReset = hash.includes("type=recovery");

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      if (isSignUp) {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/portal/auth`
          }
        });
        if (error) throw error;
        toast({
          title: "Check your email",
          description: "We've sent you a verification link.",
        });
      } else {
        await signIn(email, password, rememberMe);
        navigate("/portal");
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/portal/auth`,
      });
      
      if (error) throw error;
      
      toast({
        title: "Check your email",
        description: "We've sent you password reset instructions.",
      });
      
      setIsForgotPassword(false);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password,
      });

      if (error) throw error;

      toast({
        title: "Password updated",
        description: "Your password has been updated successfully.",
      });

      window.history.replaceState({}, document.title, window.location.pathname);
      setTimeout(() => navigate("/portal"), 1500);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestSignUp = async () => {
    setIsLoading(true);
    try {
      await signUp("test@example.com", "password");
      toast({
        title: "Test user created",
        description: "Test user has been created successfully.",
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderForm = () => {
    if (isPasswordReset) {
      return (
        <form onSubmit={handleUpdatePassword} className="space-y-4">
          <h1 className="text-2xl font-semibold mb-6">Set new password</h1>
          
          <div className="space-y-2">
            <Label htmlFor="new-password">New Password</Label>
            <Input
              id="new-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Enter your new password"
            />
          </div>

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? "Processing..." : "Update Password"}
          </Button>
        </form>
      );
    }

    if (isForgotPassword) {
      return (
        <form onSubmit={handlePasswordReset} className="space-y-4">
          <h1 className="text-2xl font-semibold mb-6">Reset password</h1>
          
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Enter your email"
            />
          </div>

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? "Processing..." : "Send reset link"}
          </Button>

          <Button
            variant="link"
            type="button"
            className="w-full"
            onClick={() => setIsForgotPassword(false)}
          >
            Back to login
          </Button>
        </form>
      );
    }

    return (
      <form onSubmit={handleAuth} className="space-y-4">
        <h1 className="text-2xl font-semibold mb-6">
          {isSignUp ? "Create an account" : "Welcome back"}
        </h1>

        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>

        {!isSignUp && (
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="rememberMe" 
                checked={rememberMe} 
                onCheckedChange={(checked) => setRememberMe(checked === true)}
              />
              <Label 
                htmlFor="rememberMe" 
                className="text-sm font-normal cursor-pointer"
              >
                Remember me
              </Label>
            </div>
            <Button
              variant="link"
              type="button"
              className="p-0 h-auto text-sm"
              onClick={() => setIsForgotPassword(true)}
            >
              Forgot password?
            </Button>
          </div>
        )}

        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading
            ? "Loading..."
            : isSignUp
            ? "Create account"
            : "Sign in"}
        </Button>

        <div className="text-center">
          <Button
            variant="link"
            type="button"
            onClick={() => setIsSignUp(!isSignUp)}
          >
            {isSignUp
              ? "Already have an account? Sign in"
              : "Don't have an account? Sign up"}
          </Button>
        </div>

        {!isSignUp && (
          <div className="text-center">
            <Button
              variant="link"
              type="button"
              onClick={() => navigate('/portal/signup')}
            >
              New to IntentDrive? Get started with plan selection →
            </Button>
          </div>
        )}
      </form>
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-[-20%] right-[10%] w-[600px] h-[600px] bg-emerald-500/[0.05] rounded-full blur-[150px]" />
        <div className="absolute bottom-[-10%] left-[20%] w-[500px] h-[500px] bg-blue-500/[0.04] rounded-full blur-[120px]" />
      </div>
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: `radial-gradient(circle at 1px 1px, rgba(148,163,184,0.04) 1px, transparent 0)`,
        backgroundSize: '40px 40px',
      }} />

      <div className="relative z-10 max-w-md w-full space-y-8">
        <div className="text-center">
          <img src="/logo.jpg" alt="IntentDrive.ai" className="h-20 w-auto rounded-2xl mx-auto shadow-lg shadow-emerald-500/20" style={{ filter: 'brightness(1.2) sepia(1) hue-rotate(100deg) saturate(2)' }} />
          <h2 className="mt-6 text-3xl font-bold text-white">
            {isSignUp ? "Create your account" : "Welcome back"}
          </h2>
          <p className="mt-2 text-slate-400 text-sm">
            {isSignUp ? "Get started with IntentDrive.ai" : "Sign in to your IntentDrive.ai account"}
          </p>
        </div>
        <Card className="p-8 bg-slate-900/50 border-slate-800 backdrop-blur-xl rounded-2xl [&_label]:text-slate-300 [&_input]:bg-slate-800/50 [&_input]:border-slate-700 [&_input]:text-white [&_input]:placeholder-slate-500 [&_input]:rounded-xl [&_input]:focus:border-emerald-500 [&_h1]:text-white [&_button[type=submit]]:bg-gradient-to-r [&_button[type=submit]]:from-emerald-500 [&_button[type=submit]]:to-teal-500 [&_button[type=submit]]:text-white [&_button[type=submit]]:rounded-xl [&_button[type=submit]]:shadow-lg [&_button[type=submit]]:shadow-emerald-500/20">
          {renderForm()}
        </Card>
      </div>
    </div>
  );
};

export default PortalAuth;
